import {
	all,
	select,
	takeEvery,
	fork,
	StrictEffect,
	call,
	put,
	delay,
} from 'redux-saga/effects'
import packageJson from '../../../../package.json'

// action
import {
	authActions,
	userActions,
	configActions,
	mainActions,
	departmentActions,
	routeActions,
} from '@redux/store/index'

// lib
import { persistor } from '@redux/index'
import { firstMainTap, firstAdminTap } from '@redux/store/core/config.store'
import { Logger } from '@utils'
import { AccessControlManager } from '@guards'

// api
import { authApi, ResType, ParameterType } from '@api'

// schema
import {
	User,
	AccessControl,
	UserType,
	AccessControlType,
	AccessControlDbType,
} from '@schemas'

// type
import type {
	RootState,
	AuthStateType,
	ConfigStateType,
	UserMenuAccessType,
	TabType,
} from '@redux/types'

const apiLogin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await authApi.login(data)
	return res
}

const apiTokenValidation = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await authApi.tokenValidation()
	return res
}

const purgeStoredState = async (isRedirect: boolean): Promise<void> => {
	await persistor.purge().then(() => {
		if (isRedirect) {
			window.location.reload()
		}
	})
}

const apiLoginUsingAccessToken = async (
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const body = { accessToken: accessToken }
	const res: ResType | undefined = await authApi.loginUsingAccessToken({
		customHeaders: customHeaders,
		body: body,
	})

	return res
}

function* initLoginPage() {
	try {
		const isLogin: boolean = yield select(state => state.auth.isLogin)

		if (!isLogin) {
			yield put(authActions.IS_LOGIN(false))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield call(purgeStoredState, true)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* tryLoginSaga(): Generator<StrictEffect, void> {
	try {
		const loginAccount: AuthStateType['login'] | any = yield select(
			(state: RootState) => state.auth.login as AuthStateType['login'],
		)

		const body = {
			email: loginAccount.email,
			password: loginAccount.password,
		}
		const apiRes: ResType | any = yield call(apiLogin, { body: { ...body } })

		if (apiRes.status === 201) {
			const data = apiRes.data[0]

			const token = { accessToken: data.accessToken, refreshToken: data.refreshToken }
			const user = new User({ ...data })
			const accessControlsData: AccessControlDbType[] = data['accessControls']
			const accessControls: AccessControlType[] = accessControlsData.map(
				(anAccess: any) => {
					return new AccessControl({
						...anAccess,
						menuI18n: anAccess.menuI18n ?? [],
					})
				},
			)

			yield put(authActions.TOKEN(token))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield put(userActions.USER(user))
			yield put(authActions.ACCESS_CONTROLS(accessControls))
			yield put(authActions.IS_LOGIN(true))
		} else if (apiRes.status === 0) {
			yield put(authActions.LOGIN_ERROR_MESSAGE('서버와 연결이 되지 않았습니다.'))
		} else {
			yield put(authActions.LOGIN_ERROR_MESSAGE('아이디와 패스워드를 다시 확인해주세요.'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* runLoginUsingAccessToken(): Generator<StrictEffect, void> {
	try {
		const data: AuthStateType['loginUsingAccessToken'] | any = yield select(
			(state: RootState) => state.auth.loginUsingAccessToken,
		)
		const accessToken: string = data !== undefined ? (data.accessToken as string) : ''

		yield call(purgeStoredState, false)
		yield put(configActions.IS_LOADING(true))

		const apiRes: ResType | any = yield call(apiLoginUsingAccessToken, accessToken)

		if (apiRes.status === 201) {
			const data = apiRes.data[0]
			const token = { accessToken: data.accessToken, refreshToken: data.refreshToken }
			const user = new User({ ...data })
			const accessControlsData: AccessControlDbType[] = data['accessControls']
			const accessControls: AccessControlType[] = accessControlsData.map(
				(anAccess: any) => {
					return new AccessControl({
						...anAccess,
						menuI18n: anAccess.menuI18n ?? [],
					})
				},
			)

			yield put(authActions.TOKEN(token))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield put(userActions.USER(user))
			yield put(authActions.ACCESS_CONTROLS(accessControls))
			yield put(authActions.IS_LOGIN(true))
		} else {
			yield put(authActions.IS_LOGIN(false))
			yield call(purgeStoredState, true)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* isLoginSaga() {
	try {
		const isLogin: boolean = yield select(state => state.auth.isLogin)
		const mode: ConfigStateType['mode'] = yield select(state => state.config.mode)
		const defaultTab: TabType = mode === 'main' ? firstMainTap : firstAdminTap

		localStorage.setItem('isLogin', isLogin.toString())

		if (isLogin === true) {
			// login
			const user: UserType = yield select(state => state.user.user)
			const accessToken: string = localStorage.getItem('accessToken') ?? ''

			yield put(departmentActions.TRIGGER_GET_DEPARTMENT_LIST(true))
			yield put(mainActions.TRIGGER_GET_MENU(true))

			if (user.id === '' && accessToken !== '') {
				// fail
				yield put(authActions.LOGIN_USING_accessToken({ accessToken: accessToken }))
			} else if (user.id === '' && accessToken === '') {
				yield put(authActions.TRIGGER_LOGOUT(true))
			}
		} else {
			// logout
			yield call(purgeStoredState, false)

			if (mode !== 'main') {
				yield put(configActions.MODE('main'))
			}

			yield put(authActions.TOKEN({ accessToken: '', refreshToken: '' }))
			yield put(configActions.SELECTED_TAB_IDX('1'))
			yield put(configActions.TAB_LIST([defaultTab]))
			yield put(userActions.LANGUAGE(''))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* runLogout() {
	try {
		const triggerLogin: boolean = yield select(state => state.auth.triggerLogin)

		if (triggerLogin) {
			yield put(authActions.IS_LOGIN(false))
			yield call(purgeStoredState, true)
			yield put(authActions.TRIGGER_LOGOUT(false))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/** 권한 비교 키 — 그룹·메뉴·CRUD 만 본다. 순서와 나머지 필드는 무시 */
const accessKey = (list: AccessControlType[]): string =>
	list
		.map(a => `${a.accessGroupId}|${a.menuId}|${a.crud}`)
		.sort()
		.join(',')

/**
 * 권한 폴링 — 로그인 중이면 60초마다 토큰 검증을 한 번 더 불러 권한을 새로 받는다.
 * 화면을 옮길 때는 LoginAccessGuard 가 이미 부르므로, 이 루프는 가만히 있는 사람을 위한 것.
 * 앱이 뜰 때 루트에서 한 번 fork 해 계속 돈다(리로드 뒤엔 IS_LOGIN 이 다시 오지 않아서
 * 액션에 걸면 시작이 안 된다). 로그아웃 상태·숨은 탭이면 그 회차는 건너뛴다.
 */
const ACCESS_POLL_MS = 60 * 1000
function* accessPollSaga() {
	while (true) {
		try {
			yield delay(ACCESS_POLL_MS)
			const isLogin: boolean = yield select(state => state.auth.isLogin)
			const storageIsLogin: boolean = localStorage.getItem('isLogin') === 'true'
			if (!isLogin || !storageIsLogin) continue
			if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
				continue
			}
			yield put(authActions.TRIGGER_CHECK_ACCESS_TOKEN(true))
		} catch (error) {
			Logger.warn(error)
		}
	}
}

function* checkAccessTokenSaga() {
	try {
		const localVersion = localStorage.getItem('systemVersion')
		if (localVersion === null || packageJson.version !== localVersion) {
			localStorage.setItem('systemVersion', packageJson.version)
			yield call(purgeStoredState, true)
		}

		const apiRes: ResType = yield call(apiTokenValidation)

		if (apiRes.status !== 201) {
			yield put(authActions.IS_LOGIN(false))
		} else {
			/*
				서버는 토큰 검증 응답에 이 계정의 최신 권한 목록을 실어 보낸다.
				전에는 달라진 것을 감지하고도 옛 목록을 다시 저장해서(버그) 권한 변경이
				재로그인 전엔 반영되지 않았다. 지금은 서버 목록으로 갈아 끼운다 —
				그러면 accessControlsSaga 가 접근 메뉴를 다시 계산하고 사이드바·가드가 따라온다.
				비교는 권한을 정하는 세 값만 정규화해서 한다(서버 행과 저장된 인스턴스는 모양이 다르다).
			*/
			const prevAccessControls: AccessControlType[] = yield select(
				state => state.auth.accessControls,
			)
			const nextAccessControls: AccessControlType[] = (
				(apiRes.data[0]?.accessControls ?? []) as AccessControlDbType[]
			).map(
				(anAccess: any) =>
					new AccessControl({ ...anAccess, menuI18n: anAccess.menuI18n ?? [] }),
			)

			if (accessKey(prevAccessControls) !== accessKey(nextAccessControls)) {
				yield put(authActions.ACCESS_CONTROLS(nextAccessControls))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* authLoadingPageSaga() {
	try {
		const isLogin: boolean = yield select(state => state.auth.isLogin)
		const accessToken = localStorage.getItem('accessToken') ?? ''
		const apiRes: ResType = yield call(apiLoginUsingAccessToken, accessToken)

		if (apiRes.status === 201) {
			const data = apiRes.data[0]
			const token = { accessToken: data.accessToken, refreshToken: data.refreshToken }
			const user = new User({ ...(data as any) })
			const accessControlsData: AccessControlDbType[] = data['accessControls']
			const accessControls: AccessControlType[] = accessControlsData.map(
				(anAccess: any) => {
					return new AccessControl({
						...anAccess,
						menuI18n: anAccess.menuI18n ?? [],
					})
				},
			)

			yield put(authActions.TOKEN(token))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield put(userActions.USER(user))
			yield put(authActions.ACCESS_CONTROLS(accessControls))
			if (!isLogin) {
				yield put(authActions.IS_LOGIN(true))
			}
			delay(1000)
			yield put(routeActions.NAVIGATE_PATH('/main/home'))
		} else {
			yield put(authActions.IS_LOGIN(false))
			delay(1000)
			yield put(routeActions.NAVIGATE_PATH('/auth/login'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* accessControlsSaga() {
	try {
		const accessControls: AccessControlType[] = yield select(
			state => state.auth.accessControls,
		)

		const newUserAccessMenuList: UserMenuAccessType[] =
			AccessControlManager.getUserMenuAccessList(accessControls)

		yield put(authActions.USER_ACCESS_MENU_LIST(newUserAccessMenuList))
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	// trigger
	yield takeEvery(authActions.TRIGGER_INIT_LOGIN_PAGE, initLoginPage)
	yield takeEvery(authActions.TRIGGER_LOGOUT, runLogout)
	yield takeEvery(authActions.TRIGGER_CHECK_ACCESS_TOKEN, checkAccessTokenSaga)
	yield takeEvery(authActions.TRIGGER_AUTH_LOADING_PAGE, authLoadingPageSaga)

	// login
	yield takeEvery(authActions.IS_LOGIN, isLoginSaga)
	yield takeEvery(authActions.LOGIN, tryLoginSaga)

	// token
	yield takeEvery(authActions.LOGIN_USING_accessToken, runLoginUsingAccessToken)

	// access controls
	yield takeEvery(authActions.ACCESS_CONTROLS, accessControlsSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga), fork(accessPollSaga)])
}
